import React from "react";

import { CheckOutlined, DownOutlined } from "@ant-design/icons";
import {
  Button, Checkbox, Dropdown, Switch,
} from "antd";

import { AccessStatusContext } from "../../../grace-period/hooks/useAccessStatus";
import SpecButton from "./SpecButton";

import { hasPermission, permissions } from "../../../../utils/Permissions";

import { ReactComponent as AlertBellIcon } from "../../../../assets/images/product/AlertBellIcon.svg";
import cycleIcon from "../../../../assets/images/product/circleArrow.png";

import "./SpecsBlock.css";

const ALERT_FILTER_OPTIONS = [
  { key: "all", label: "All Alerts" },
  { key: "within_2_days", label: "≤ 2 days" },
  { key: "due_today", label: "Due today" },
];

const ALERT_CHILD_KEYS = ["within_2_days", "due_today"];
const ALL_ALERT_KEYS = ["all", ...ALERT_CHILD_KEYS];

const getReviewResultsAlertParam = (alertFilters = []) => {
  const selected = new Set(alertFilters);
  if (selected.has("all") || ALERT_CHILD_KEYS.every((key) => selected.has(key))) {
    return "all";
  }
  if (selected.has("due_today")) {
    return "1";
  }
  if (selected.has("within_2_days")) {
    return "2";
  }
  return "";
};

class SpecsBlock extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      alertFilters: [],
      alertDropdownOpen: false,
    };
  }

  handleToggleFilter = (index) => {
    const { handleFilterBySpec } = this.props;
    handleFilterBySpec(index);
  };

  handleAlertDropdownOpenChange = (open) => {
    this.setState({ alertDropdownOpen: open });
  };

  notifyAlertFilterChange = (alertFilters) => {
    const { handleFilterByAlert } = this.props;
    if (typeof handleFilterByAlert !== "function") {
      return;
    }
    handleFilterByAlert(getReviewResultsAlertParam(alertFilters));
  };

  handleAlertFilterChange = (key) => {
    this.setState((prev) => {
      const selected = new Set(prev.alertFilters);

      if (key === "all") {
        if (ALERT_CHILD_KEYS.every((childKey) => selected.has(childKey))) {
          return { alertFilters: [] };
        }
        return { alertFilters: [...ALL_ALERT_KEYS] };
      }

      if (selected.has(key)) {
        selected.delete(key);
      } else {
        selected.add(key);
      }

      if (ALERT_CHILD_KEYS.every((childKey) => selected.has(childKey))) {
        selected.add("all");
      } else {
        selected.delete("all");
      }

      return { alertFilters: Array.from(selected) };
    }, () => {
      const { alertFilters } = this.state;
      this.notifyAlertFilterChange(alertFilters);
    });
  };

  renderAlertMenu = () => {
    const { alertFilters } = this.state;

    return (
      <div className="SpecsBlock__AlertMenu">
        {ALERT_FILTER_OPTIONS.map(({ key, label }) => {
          const isSelected = alertFilters.includes(key);
          return (
            <div
              key={key}
              className="SpecsBlock__AlertItem"
              onClick={() => this.handleAlertFilterChange(key)}
            >
              <Checkbox
                className="SpecsBlock__AlertCheckbox"
                checked={isSelected}
                onClick={(event) => event.preventDefault()}
              >
                <span className="SpecsBlock__AlertItemLabel">{label}</span>
              </Checkbox>
              {isSelected && (
                <CheckOutlined className="SpecsBlock__AlertItemCheck" aria-hidden />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  render() {
    const {
      specSelected,
      handleSpecManagerToggle,
      handleFilterByRetest,
    } = this.props;
    const { alertDropdownOpen, alertFilters } = this.state;
    const selectedSpecFlags = specSelected ? String(specSelected).split(",").filter(Boolean) : [];
    const { areResultsLocked } = this.context || {};
    const lockFilters = Boolean(areResultsLocked);

    return (
      <div className="SpecsBlockContainer">
        <div className="reportSliderContainer">
          <span className="retestSpan">Retest Request</span>
          <Switch
            onClick={handleFilterByRetest}
            className="RetestRequestsSwitch"
            disabled={lockFilters || selectedSpecFlags.includes("3")}
            size="small"
          />
        </div>
        <Dropdown
          trigger={["click"]}
          open={alertDropdownOpen}
          onOpenChange={this.handleAlertDropdownOpenChange}
          popupClassName="SpecsBlock__AlertDropdown"
          popupRender={this.renderAlertMenu}
        >
          <Button
            type="default"
            className={`SpecsBlock__AlertBtn${alertDropdownOpen ? " SpecsBlock__AlertBtn--open" : ""}${alertFilters.length ? " SpecsBlock__AlertBtn--selected" : ""}`}
          >
            <AlertBellIcon className="SpecsBlock__AlertBtnIcon" aria-hidden />
            <span className="SpecsBlock__AlertBtnLabel">Alert</span>
            <DownOutlined
              className={`SpecsBlock__AlertBtnChevron${alertDropdownOpen ? " SpecsBlock__AlertBtnChevron--open" : ""}`}
              aria-hidden
            />
          </Button>
        </Dropdown>
        <div className="SpecsBlock__SpecButtonBlock">
          <SpecButton
            index="1"
            specText="In Spec"
            specColor="var(--color-content-positive)"
            specSelected={specSelected}
            disableFilterButton={lockFilters}
            handleToggleFilter={this.handleToggleFilter}
          />
          <SpecButton
            index="2"
            specText="Out of Spec"
            specColor="var(--color-content-negative)"
            specSelected={specSelected}
            disableFilterButton={lockFilters}
            handleToggleFilter={this.handleToggleFilter}
          />
          <SpecButton
            index="0"
            specText="In Process"
            specColor="var(--color-content-warning)"
            specSelected={specSelected}
            disableFilterButton={lockFilters}
            handleToggleFilter={this.handleToggleFilter}
          />
        </div>
        {hasPermission(permissions.product.specManager) && (
          <Button
            type="primary"
            id="productReportSpecManagerButton"
            className="ProductReport__SpecManagerButton"
            icon={<img src={cycleIcon} alt="cycleIcon" width="14px" />}
            disabled={lockFilters}
            onClick={lockFilters ? undefined : handleSpecManagerToggle}
          >
            Spec Manager
          </Button>
        )}
      </div>
    );
  }
}

SpecsBlock.contextType = AccessStatusContext;

export default SpecsBlock;
